import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, uploadFileUrl } from "@/lib/api";
import { Loader2, Plus, Trash2, Upload, GripVertical, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const AdminGallery = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [form, setForm] = useState({ image_url: "", caption: "", category: "", order: 0 });
    const fileRef = useRef();

    const load = async () => {
        setLoading(true);
        const { data } = await api.get("/admin/gallery");
        setItems(data.items || []);
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!form.image_url) { toast.error("Please provide an image URL or upload"); return; }
        setSaving(true);
        try {
            await api.post("/admin/gallery", { ...form, order: Number(form.order) || 0, is_active: true });
            setForm({ image_url: "", caption: "", category: "", order: 0 });
            toast.success("Added to gallery");
            load();
        } catch { toast.error("Failed to add"); }
        finally { setSaving(false); }
    };

    const upload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            form.append("kind", "gallery");
            const { data } = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
            setForm((f) => ({ ...f, image_url: uploadFileUrl(data.path) }));
            toast.success("Uploaded — click Add to save");
        } catch { toast.error("Upload failed"); }
        finally { setUploading(false); }
    };

    const toggleActive = async (item) => {
        try {
            await api.patch(`/admin/gallery/${item.id}`, { is_active: !item.is_active });
            setItems((xs) => xs.map((x) => x.id === item.id ? { ...x, is_active: !x.is_active } : x));
        } catch { toast.error("Failed to update"); }
    };

    const del = async (item) => {
        if (!confirm("Remove this image from the gallery?")) return;
        try {
            await api.delete(`/admin/gallery/${item.id}`);
            setItems((xs) => xs.filter((x) => x.id !== item.id));
            toast.success("Removed");
        } catch { toast.error("Failed to remove"); }
    };

    const updateOrder = async (item, order) => {
        const numeric = Number(order) || 0;
        try {
            await api.patch(`/admin/gallery/${item.id}`, { order: numeric });
            setItems((xs) => xs.map((x) => x.id === item.id ? { ...x, order: numeric } : x));
        } catch { toast.error("Failed"); }
    };

    return (
        <div className="p-8" data-testid="admin-gallery">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Gallery</h1>
                    <p className="text-sm text-muted-foreground">Manage the public Gallery page — {items.length} image{items.length === 1 ? "" : "s"}</p>
                </div>
            </div>

            {/* Add form */}
            <div className="rounded-2xl bg-card border p-5 mb-6" data-testid="add-gallery-form">
                <h3 className="font-display text-lg font-semibold mb-4">Add a new image</h3>
                <div className="grid md:grid-cols-[1fr_1fr_1fr_100px_auto] gap-3 items-end">
                    <div>
                        <div className="text-xs text-muted-foreground mb-1">Image URL</div>
                        <Input placeholder="https://..." value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="gallery-url" className="rounded-xl h-10" />
                    </div>
                    <div>
                        <div className="text-xs text-muted-foreground mb-1">Caption (optional)</div>
                        <Input placeholder="e.g. Ananya's anniversary miniature" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} className="rounded-xl h-10" />
                    </div>
                    <div>
                        <div className="text-xs text-muted-foreground mb-1">Category (optional)</div>
                        <Input placeholder="single, couple, wedding…" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-xl h-10" />
                    </div>
                    <div>
                        <div className="text-xs text-muted-foreground mb-1">Order</div>
                        <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="rounded-xl h-10" />
                    </div>
                    <div className="flex gap-2">
                        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
                        <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="gallery-upload-btn" className="rounded-full h-10">
                            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4" /> Upload</>}
                        </Button>
                        <Button onClick={create} disabled={saving} data-testid="gallery-add-btn" className="rounded-full h-10 bg-primary hover:bg-primary/90">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" /> Add</>}
                        </Button>
                    </div>
                </div>
                {form.image_url && (
                    <div className="mt-4 flex items-center gap-3">
                        <img src={form.image_url} alt="preview" className="w-24 h-24 rounded-2xl object-cover border" />
                        <div className="text-xs text-muted-foreground">Preview — click Add to save</div>
                    </div>
                )}
            </div>

            {/* Gallery grid */}
            {loading ? (
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
            ) : items.length === 0 ? (
                <div className="rounded-2xl bg-card border p-14 text-center">
                    <ImageIcon className="w-8 h-8 mx-auto opacity-40 mb-2" />
                    <p className="text-sm text-muted-foreground">No images yet — add your first above.</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {items.map((item) => (
                        <div key={item.id} className={`rounded-2xl bg-card border overflow-hidden group ${item.is_active ? "" : "opacity-50"}`} data-testid={`gallery-item-${item.id}`}>
                            <div className="aspect-[4/5] relative overflow-hidden bg-brand-cream">
                                <img src={item.image_url} alt={item.caption || ""} className="w-full h-full object-cover" />
                                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-black/50 transition-opacity flex items-center justify-center gap-2">
                                    <button onClick={() => toggleActive(item)} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-white text-primary">
                                        {item.is_active ? "Hide" : "Show"}
                                    </button>
                                    <button onClick={() => del(item)} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-destructive text-destructive-foreground"><Trash2 className="w-3 h-3 inline" /> Delete</button>
                                </div>
                                {!item.is_active && (
                                    <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-widest bg-muted text-muted-foreground px-2 py-0.5 rounded-full">Hidden</span>
                                )}
                            </div>
                            <div className="p-3 space-y-1">
                                {item.caption && <div className="text-sm font-semibold line-clamp-1">{item.caption}</div>}
                                <div className="flex items-center justify-between text-xs text-muted-foreground">
                                    <span>{item.category || "—"}</span>
                                    <div className="flex items-center gap-1">
                                        <GripVertical className="w-3 h-3" />
                                        <input type="number" value={item.order} onChange={(e) => updateOrder(item, e.target.value)} className="w-14 h-7 rounded-lg border px-2 text-xs" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AdminGallery;
